// Shared by the driver (delivery) and customer (pickup) ready-time pickers:
// confirmed saves and the time cards for scheduled orders.
import React, {useState, useEffect} from 'react'
import firebase from '../../firebase'
import {Button} from "@blueprintjs/core"
import {DateTime} from 'luxon'

import './PickupTimeEstimate.scss'

export const MINUTE = 60 * 1000
const SAVE_TIMEOUT_MS = 5 * 1000

export function displayMilli(ts) {
  return DateTime.fromMillis(ts).toFormat('EEE hh:mma')
}

// day only when it isn't today: "10:40AM" or "Fri 10:40AM"
export function displayChoiceTime (ts) {
  const time = DateTime.fromMillis(ts)
  return time.hasSame(DateTime.local(), 'day') ? time.toFormat('h:mma') : time.toFormat('EEE h:mma')
}

export function floorToFiveMinutes (ts) {
  return Math.floor(ts / (5 * MINUTE)) * (5 * MINUTE)
}

// Save status lives outside the component so it survives navigating between orders.
const saveStatusByOrder = {}
const saveStatusListeners = new Set()

function setSaveStatus (orderId, status) {
  saveStatusByOrder[orderId] = status
  saveStatusListeners.forEach((listener) => listener())
}

function useSaveStatus (orderId) {
  const [, rerender] = useState(0)
  useEffect(() => {
    const listener = () => rerender((n) => n + 1)
    saveStatusListeners.add(listener)
    return () => { saveStatusListeners.delete(listener) }
  }, [])
  return saveStatusByOrder[orderId] || {state: 'idle'}
}

// One multi-path update so the order and notify records save together or not at all.
// The UI shows the value immediately (local write), so only call it saved once the server confirms.
export function saveUpdates (orderId, updates, afterSave) {
  const attempt = Date.now()
  const isCurrentAttempt = () => (saveStatusByOrder[orderId] || {}).attempt === attempt
  setSaveStatus(orderId, {state: 'saving', attempt, updates, afterSave})

  setTimeout(() => {
    if (isCurrentAttempt() && saveStatusByOrder[orderId].state === 'saving') {
      setSaveStatus(orderId, {state: 'failed', attempt, updates, afterSave})
    }
  }, SAVE_TIMEOUT_MS)

  firebase.database().ref().update(updates)
    .then(() => {
      if (!isCurrentAttempt()) { return }
      setSaveStatus(orderId, {state: 'saved', attempt})
      afterSave && afterSave()
    })
    .catch((e) => {
      console.log('pickup time save failed', e)
      if (!isCurrentAttempt()) { return }
      setSaveStatus(orderId, {state: 'failed', attempt, updates, afterSave})
    })
}

export function SaveStatusNotice ({orderId}) {
  const saveStatus = useSaveStatus(orderId)
  if (saveStatus.state === 'saving') {
    return <p className="save-status saving">Saving…</p>
  }
  if (saveStatus.state === 'failed') {
    return (
      <div className="save-status failed">
        <p>⚠️ Not saved. Check internet, then retry.</p>
        <Button large intent="danger" icon="refresh"
          onClick={() => saveUpdates(orderId, saveStatus.updates, saveStatus.afterSave)}
          >Retry Save</Button>
      </div>
    )
  }
  return null
}

// [earlier] [recommended] [custom] [NOW]; earlier/recommended/NOW only for scheduled orders
export function TimeChoiceCards ({earlierTS, recommendedTS, showNow, onPick}) {
  const [showCustom, setShowCustom] = useState(false)
  const [customValue, setCustomValue] = useState('')
  const now = Date.now()

  function openCustom () {
    setCustomValue(DateTime.fromMillis(recommendedTS || now).toFormat("yyyy-MM-dd'T'HH:mm"))
    setShowCustom(true)
  }

  function commitCustom () {
    const ts = DateTime.fromISO(customValue).toMillis() // tablet's local time zone
    if (!Number(ts)) { return }
    onPick(ts)
    setShowCustom(false)
  }

  return (
    <React.Fragment>
      <div className="pickup-time-choices">
        {earlierTS &&
          <Button large className="pickup-time-card"
            disabled={earlierTS < now}
            onClick={() => onPick(earlierTS)}
            ><span className="pickup-time-card__time">{displayChoiceTime(earlierTS)}</span></Button>
        }
        {recommendedTS &&
          <Button large className="pickup-time-card recommended"
            disabled={recommendedTS < now}
            onClick={() => onPick(recommendedTS)}
            >
            <span className="pickup-time-card__time">{displayChoiceTime(recommendedTS)}</span>
            <span className="recommended-badge">recommended</span>
          </Button>
        }
        {!showCustom &&
          <Button large className="pickup-time-card" icon="time" onClick={openCustom}>custom</Button>
        }
        {showNow &&
          <Button large className="pickup-time-card" onClick={() => onPick(Date.now())}
            ><span className="pickup-time-card__time">NOW</span></Button>
        }
      </div>

      {showCustom &&
        <div className="custom-time">
          <input type="datetime-local" className="time"
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)} />
          <Button large intent="primary" onClick={commitCustom}>Save</Button>
          <Button large onClick={() => setShowCustom(false)}>Cancel</Button>
        </div>
      }
    </React.Fragment>
  )
}
